/**
 * Story 30.3 — this browser's PushSubscription.
 *
 *   currentSubscription()      the registration's subscription, or null
 *   subscribeThisDevice(key)   pushManager.subscribe with the server's VAPID
 *                              public key (no permission prompt of its own:
 *                              the caller already holds 'granted')
 *   endpointSha256(endpoint)   hex sha256 (crypto.subtle) — the server lists
 *                              devices by this hash, never by endpoint
 *   hasApplicationServerKey()  does a subscription use this VAPID key?
 *   subscribeBody()            the POST body from a subscription
 *   forgetThisDevice()         logout: `unsubscribe()` first, then a best-effort
 *                              DELETE of this device's row; capped at 1.5s, never throws
 *
 * The endpoint is a bearer capability: nothing here logs it.
 */

import {
  deletePushSubscriptionBare,
  fetchPushSubscriptionsBare,
  type PushSubscribeBody,
} from '@/lib/api/pushSubscriptions';
import { existingPushWorker, registerPushWorker } from './register';

export const FORGET_DEVICE_TIMEOUT_MS = 1500;

export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=');
  const raw = atob(padded);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let raw = '';
  for (const b of bytes) raw += String.fromCharCode(b);
  return btoa(raw).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function endpointSha256(endpoint: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(endpoint));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

export async function currentSubscription(
  registration?: ServiceWorkerRegistration | null,
): Promise<PushSubscription | null> {
  const reg = registration === undefined ? await registerPushWorker() : registration;
  if (!reg?.pushManager) return null;
  try {
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export async function subscribeThisDevice(publicKey: string): Promise<PushSubscription> {
  const reg = await registerPushWorker();
  if (!reg?.pushManager) throw new Error('push: no service worker registration');
  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64UrlToBytes(publicKey),
  });
}

/**
 * True when `subscription` was made with `publicKey`. A browser that does not
 * report the key (`options.applicationServerKey` null) counts as a match: it
 * cannot be compared, and treating it as rotated would re-subscribe on every
 * open.
 */
export function hasApplicationServerKey(subscription: PushSubscription, publicKey: string): boolean {
  const current = subscription.options?.applicationServerKey;
  if (!current) return true;
  const a = new Uint8Array(current);
  let b: Uint8Array;
  try {
    b = base64UrlToBytes(publicKey);
  } catch {
    return false;
  }
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function keyOf(subscription: PushSubscription, name: 'p256dh' | 'auth'): string {
  const json = typeof subscription.toJSON === 'function' ? subscription.toJSON() : undefined;
  const fromJson = json?.keys?.[name];
  if (typeof fromJson === 'string' && fromJson) return fromJson;
  const raw = subscription.getKey?.(name);
  return raw ? bytesToBase64Url(new Uint8Array(raw)) : '';
}

export function subscribeBody(subscription: PushSubscription, replacesEndpoint?: string): PushSubscribeBody {
  const body: PushSubscribeBody = {
    endpoint: subscription.endpoint,
    keys: { p256dh: keyOf(subscription, 'p256dh'), auth: keyOf(subscription, 'auth') },
  };
  if (replacesEndpoint && replacesEndpoint !== subscription.endpoint) body.replacesEndpoint = replacesEndpoint;
  return body;
}

/** Unsubscribe, swallowing any failure. */
export async function unsubscribeQuietly(subscription: PushSubscription | null): Promise<void> {
  if (!subscription) return;
  try {
    await subscription.unsubscribe();
  } catch (err) {
    console.warn('push: unsubscribe failed', err instanceof Error ? err.message : err);
  }
}

/**
 * Logout (ruling O14): always `unsubscribe()` (first, before any network
 * call), then delete this device's row (best effort), so the next person on a shared device gets nothing meant
 * for the last one. Capped at 1.5s and never throws: signing out must never
 * wait on, or fail because of, push. Never registers a worker.
 */
export async function forgetThisDevice(accessToken: string | null): Promise<void> {
  const controller = typeof AbortController === 'undefined' ? null : new AbortController();
  const work = (async () => {
    const subscription = await currentSubscription(await existingPushWorker());
    if (!subscription) return;
    // Hash, then unsubscribe FIRST: the server round trip below may be cut
    // off by the cap, but the browser must never keep the subscription (O14).
    const hash = await endpointSha256(subscription.endpoint);
    await unsubscribeQuietly(subscription);
    if (!accessToken) return;
    try {
      const devices = await fetchPushSubscriptionsBare(accessToken, controller?.signal);
      const mine = devices.find((d) => d.endpointSha256 === hash);
      if (mine) await deletePushSubscriptionBare(mine.id, accessToken, controller?.signal);
    } catch (err) {
      console.warn('push: could not remove this device on sign-out', err instanceof Error ? err.message : err);
    }
  })().catch(() => undefined);

  let timer: ReturnType<typeof setTimeout> | undefined;
  const cap = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      controller?.abort();
      resolve();
    }, FORGET_DEVICE_TIMEOUT_MS);
  });
  try {
    await Promise.race([work, cap]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
